/**
 * The share row (M17, PRD §1.8) — one artefact, two ways to move it.
 *
 * The card itself sits above, bordered and quiet, so it reads as a preview of a
 * post and not as a banner. Underneath it: copy the link, or hand it to X's
 * composer. Amber is spent on the one primary action and nowhere else.
 *
 * A pure render of what it is handed. Everything that talks to the network — the
 * minting of a token-carrying link, the reading of a door's public totals — lives
 * in the containers beside it, which is what lets every row be asserted directly.
 */
import { ChipButton } from "../ui/button";
import { CopyButton } from "../ui/copy-button";
import { postOnXUrl } from "./labels";
import type { ShareCardKind } from "../../lib/telemetry";
import { track } from "../../lib/telemetry";

export const POST_LABEL = "Post on X";
export const COPY_LABEL = "Copy link";

type Props = {
  /** The OG image, absolute. Rendered lazily — a share row is never the point of a screen. */
  imageUrl: string;
  /** What the card says, for anyone who cannot see it. */
  imageAlt: string;
  /** The page that gets posted. Copied verbatim and handed to X verbatim. */
  shareUrl: string;
  /** The suggested post, in Nob's register. The poster may say anything they like. */
  postText: string;
  /** Which loop this is, for the one telemetry event the share carries. */
  kind: ShareCardKind;
  /**
   * The artefact was actually moved — a link on a clipboard, or a post handed
   * to X's composer. Optional, and only ever a report of something that
   * happened: the doorman's "hand out your door" prompt is answered by this and
   * by nothing else, least of all by the row having been rendered.
   */
  onShared?: () => void;
};

export function ShareRow({ imageUrl, imageAlt, shareUrl, postText, kind, onShared }: Props) {
  return (
    <div className="mt-4">
      <img
        src={imageUrl}
        alt={imageAlt}
        loading="lazy"
        className="block w-full max-w-full rounded-lg border border-border"
      />
      <div className="mt-3 flex flex-wrap items-center gap-2">
        <CopyButton
          value={shareUrl}
          label={COPY_LABEL}
          {...(onShared ? { onCopied: onShared } : {})}
        />
        <PostOnX
          text={postText}
          url={shareUrl}
          kind={kind}
          {...(onShared ? { onPosted: onShared } : {})}
        />
      </div>
    </div>
  );
}

/**
 * Hands the composer the suggested line and the link. A new tab, opener severed:
 * the post is theirs to write, and this app has no business in that window.
 */
export function PostOnX({
  text,
  url,
  kind,
  onPosted,
}: {
  text: string;
  url: string;
  kind: ShareCardKind;
  onPosted?: () => void;
}) {
  return (
    <ChipButton
      type="button"
      onClick={() => {
        track({ name: "share_card_generated", kind, posted: true });
        if (typeof window !== "undefined") {
          window.open(postOnXUrl(text, url), "_blank", "noopener,noreferrer");
        }
        onPosted?.();
      }}
    >
      {POST_LABEL}
    </ChipButton>
  );
}
